import { getByID } from '@adornis/baseql/operations/mongo.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import { getIntercomEntityById } from '../../api/intercom.js';
import { ContactEntity } from '../../entities/contact.js';
import { OdooEntity } from '../../entities/odoo.js';

export const CommentDetailView = stateful(
  ({ selected, onClose }: { selected: { source: string; id: string } | null; onClose: () => void }) => {
    if (!selected) return nothing;

    console.log('detail view: ', selected);

    // Query the appropriate entity based on source
    const entityQuery = useQuery((): Promise<any> | any => {
      if (!selected) return of(null);

      switch (selected.source) {
        case 'ODOO':
          // For Odoo, use the registered query that finds by odooTicketID
          return getByID(
            OdooEntity,
            selected.id,
          )({
            _id: 1,
            odooTicketID: 1,
            subject: 1,
            from: 1,
            createdAt: 1,
            comments: 1,
            odooTags: 1,
            team: 1,
            priority: 1,
            isCheckedBySupport: 1,
            isOdooActive: 1,
          });
        case 'INTERCOM':
          return getIntercomEntityById(selected.id)({
            _id: 1,
            intercomId: 1,
            message: 1,
            botResponse: 1,
            createdAt: 1,
            comments: 1,
            lastAuthorType: 1,
            isCheckedBySupport: 1,
            isAgentAssist: 1,
          });
        case 'CONTACT':
          return getByID(
            ContactEntity,
            selected.id,
          )({
            _id: 1,
            companyName: 1,
            email: 1,
            phone: 1,
            position: 1,
            location: 1,
            timestamp: 1,
            notes: 1,
            status: 1,
            isCheckedBySupport: 1,
            odooTicketID: 1,
          });
        default:
          return of(null);
      }
    }, [selected?.source, selected?.id]);

    // Show loading state
    if (entityQuery.loading) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body flex items-center justify-center">
            <span class="loading loading-spinner loading-lg text-secondary"></span>
          </div>
        </div>
      `;
    }

    // Show error state
    if (entityQuery.error) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-xl font-bold">Comment Details</h2>
              <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="alert alert-error mt-4">
              <span>Error loading details: ${entityQuery.error.message}</span>
            </div>
          </div>
        </div>
      `;
    }

    // Show empty state
    if (!entityQuery.value) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body h-full">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-xl font-bold">Comment Details</h2>
              <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex flex-col items-center justify-center py-12 text-center opacity-60">
              <p>Entity not found</p>
            </div>
          </div>
        </div>
      `;
    }

    const entity = entityQuery.value;

    // Render comments list
    const renderComments = (comments: string[] | null | undefined) => {
      if (!comments || comments.length === 0) {
        return html`<div class="text-sm italic text-base-content/50 p-4 text-center">No comments</div>`;
      }

      return html`
        <div class="space-y-3">
          ${comments.map(
            (comment, index) => html`
              <div class="bg-base-200 p-3 rounded-lg">
                <div class="flex justify-between items-start mb-1">
                  <div class="text-xs text-base-content/50">#${index + 1}</div>
                </div>
                <div class="text-sm whitespace-pre-wrap">${comment}</div>
              </div>
            `,
          )}
        </div>
      `;
    };

    // Render source-specific fields
    const renderSourceSpecificFields = () => {
      switch (selected.source) {
        case 'ODOO':
          const odooEntity = entity as any;
          return html`
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Odoo Ticket ID</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${odooEntity.odooTicketID || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Subject</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">${odooEntity.subject || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">From</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${odooEntity.from || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Odoo Tags</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${odooEntity.odooTags
                  ?.filter((tag: string) => !tag.includes(':') && !tag.includes('my_data'))
                  .join(', ') || 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Team</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${odooEntity.team || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Priority</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${odooEntity.priority ?? 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Checked by Support</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${odooEntity.isCheckedBySupport
                  ? html`<span class="text-secondary">✅ Yes</span>`
                  : html`<span class="text-error">❌ No</span>`}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Active in Odoo</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${odooEntity.isOdooActive !== false
                  ? html`<span class="text-secondary">✅ Yes</span>`
                  : html`<span class="text-error">❌ No</span>`}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Created At</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${odooEntity.createdAt ? format(odooEntity.createdAt, 'PPpp') : 'N/A'}
              </div>
            </div>
          `;

        case 'INTERCOM':
          const intercomEntity = entity as any;
          return html`
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Intercom ID</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${intercomEntity.intercomId || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Message</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
                ${intercomEntity.message || 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Bot Response</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
                ${intercomEntity.botResponse || 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Last Author Type</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${intercomEntity.lastAuthorType || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Agent Assist</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${intercomEntity.isAgentAssist
                  ? html`<span class="text-secondary">✅ Yes</span>`
                  : html`<span class="text-error">❌ No</span>`}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Checked by Support</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${intercomEntity.isCheckedBySupport
                  ? html`<span class="text-secondary">✅ Yes</span>`
                  : html`<span class="text-error">❌ No</span>`}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Created At</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${intercomEntity.createdAt ? format(intercomEntity.createdAt, 'PPpp') : 'N/A'}
              </div>
            </div>
          `;

        case 'CONTACT':
          const contactEntity = entity as any;
          return html`
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Odoo Ticket ID</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${contactEntity.odooTicketID || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Company Name</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${contactEntity.companyName || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Email</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${contactEntity.email
                  ? html`<a href="mailto:${contactEntity.email}" class="link link-primary">${contactEntity.email}</a>`
                  : 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Phone</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${contactEntity.phone
                  ? html`<a href="tel:${contactEntity.phone}" class="link link-primary">${contactEntity.phone}</a>`
                  : 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Position</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${contactEntity.position || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Location</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${contactEntity.location || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Status</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${contactEntity.status || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Checked by Support</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${contactEntity.isCheckedBySupport
                  ? html`<span class="text-secondary">✅ Yes</span>`
                  : html`<span class="text-error">❌ No</span>`}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Timestamp</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${contactEntity.timestamp ? format(contactEntity.timestamp, 'PPpp') : 'N/A'}
              </div>
            </div>
          `;

        default:
          return nothing;
      }
    };

    // Get comments/notes based on source
    const getComments = () => {
      if (selected.source === 'CONTACT') {
        return (entity as any).notes;
      }
      return (entity as any).comments;
    };

    return html`
      <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
        <div class="card-body h-full">
          <div class="flex justify-between items-center">
            <h2 class="card-title text-xl font-bold">Comment Details</h2>
            <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="divider my-2"></div>

          <div class="space-y-4 overflow-y-auto max-h-[calc(100vh-200px)]">
            <!-- Source Badge -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Source</span>
              </label>
              <div>
                <span class="badge badge-primary badge-lg">${selected.source}</span>
              </div>
            </div>

            <!-- Entity ID -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Entity ID</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm font-mono">${selected.id}</div>
            </div>

            <div class="divider my-2"></div>

            <!-- Source-specific fields -->
            ${renderSourceSpecificFields()}

            <div class="divider my-2"></div>

            <!-- Comments Section -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">${selected.source === 'CONTACT' ? 'Notes' : 'Comments'}</span>
                <span class="label-text-alt">${getComments()?.length || 0} total</span>
              </label>
              <div class="bg-base-200 rounded-lg p-2">${renderComments(getComments())}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  },
);
